
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/lib/blogUtils";
import { BlogPost } from "@/types/blog";
import { Skeleton } from "@/components/ui/skeleton";
import { useState, useEffect } from "react";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { decodeHtmlEntities } from "@/utils/htmlUtils";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

export interface FeaturedPostProps {
  post: BlogPost;
}

export function FeaturedPost({ post }: FeaturedPostProps) {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [optimizedImageUrl, setOptimizedImageUrl] = useState("");
  
  // Format post date
  const formattedDate = formatDate(post.date);
  
  // Clean excerpt text - ensure HTML is removed and entities are decoded
  const cleanExcerpt = decodeHtmlEntities(post.excerpt.replace(/<[^>]+>/g, ''));
  
  // Set initial state
  useEffect(() => {
    if (!post.featuredImage) {
      setImageError(true);
      setImageLoading(false);
    }
  }, [post.featuredImage]);

  return (
    <div className="relative rounded-xl overflow-hidden bg-background hover:shadow-lg transition-shadow duration-300 border border-border">
      <Link to={post.url || `/blog/${post.slug}`} className="block">
        <div className="flex flex-col md:flex-row">
          <div className="md:w-1/2 lg:w-3/5 relative">
            {/* Image container with loading state and hover zoom effect */}
            <div className="aspect-[16/9] w-full relative overflow-hidden">
              {imageLoading && (
                <Skeleton className="absolute inset-0" />
              )}
              
              {!imageError && (
                <OptimizedImage
                  src={post.featuredImage}
                  alt={post.title}
                  width={1600}
                  height={900} // 16:9 aspect ratio
                  className="hover:scale-110 transition-all duration-500"
                  onLoad={() => setImageLoading(false)}
                  onError={() => {
                    console.error("[FeaturedPost] Failed to load image:", post.featuredImage);
                    setImageError(true);
                    setImageLoading(false);
                  }}
                />
              )}
              
              {imageError && (
                <div className="w-full h-full flex items-center justify-center bg-muted">
                  <span className="text-muted-foreground text-base">Immagine non disponibile</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="p-6 md:w-1/2 lg:w-2/5 flex flex-col justify-center">
            {post.categories && post.categories.length > 0 && (
              <div className="mb-3">
                <Badge className="bg-primary hover:bg-primary/90 text-white">
                  {post.categories[0]}
                </Badge>
              </div>
            )}
            
            <h2 className="text-2xl md:text-3xl font-bold mb-3 line-clamp-3 group-hover:text-primary transition-colors">
              {post.title}
            </h2>
            
            <p className="text-muted-foreground mb-4 line-clamp-3">
              {cleanExcerpt}
            </p>
            
            <div className="flex items-center text-sm text-muted-foreground mt-auto mb-4">
              <span>{formattedDate}</span>
              <span className="mx-2">•</span>
              <span>{post.readingTime || "5 min"} di lettura</span>
            </div>
            
            <Button 
              variant="outline" 
              className="w-full sm:w-auto mt-2 border-primary text-primary hover:bg-primary/10 hover:text-primary"
            >
              Leggi tutto <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </Link>
    </div>
  );
}
